/* eslint-disable no-plusplus */
// third-party
import { createSlice } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import { returnFiltersQueryString } from 'helpers/objectFunctions';

// project imports
import { ARYABHATTA_API_DOMAIN_V1 } from 'config';
import { devConsolelog } from 'helpers/utilityFunctions';
import axios from 'utils/axios';
import { DEFAULT_ERROR_MESSAGE } from '../constant';
import { dispatch } from '../index';
import { openSnackbar } from './snackbar';

// ----------------------------------------------------------------------

const initialState = {
    error: null,
    data: {},
    memberFollowUpData: {},
    requirementsData: {},
    isLoading: true,
    agentFollowUpFilters: {
        next_action_at: {
            start_date: format(new Date(), 'yyyy-MM-dd'),
            end_date: format(new Date(), 'yyyy-MM-dd'),
            isDateFilter: true
        },
        campaign: {
            campaign: { id: 0, name: 'Campaign All' }
        }
    },
    requirementFilters: {
        next_follow_up_on: {
            start_date: format(new Date(), 'yyyy-MM-dd'),
            end_date: format(new Date(), 'yyyy-MM-dd'),
            isDateFilter: true
        },
        current_status: {
            current_status: { id: 0, name: 'All Requirements' }
        }
    }
};

const slice = createSlice({
    name: 'acmDashboard',
    initialState,
    reducers: {
        // HAS ERROR
        hasError(state, action) {
            state.error = action.payload;
        },

        // setSummaryData
        setData(state, action) {
            const { response = {} } = action.payload;
            const { acm_profile: acmProfile = {}, stats = {} } = response;
            const { first_name: firstName = '', last_name: lastName = '', email = '', ab_id: abId = null } = acmProfile;
            state.data.acmProfile = { firstName, lastName, email, abId };
            state.data.monthlyGmv = stats.monthly_gmv;
            state.data.transactingAgents = stats.transacting_agent;
        },

        // set agent follow up data
        setAgentFollowUpData(state, action) {
            const { count = 0, next = null, previous = null, results = {}, page = 0, page_size: rowsPerPage = 0 } = action.payload;
            const { utilities = {}, members_list: membersList = [] } = results || {};
            const { filters: followUpFilters = [] } = utilities || {};
            const { campaign: statusArray = [] } = followUpFilters[0] || [];
            statusArray.unshift({ id: 0, name: 'Campaign All' });
            devConsolelog('follow up data22', statusArray);
            state.memberFollowUpData.count = count;
            state.memberFollowUpData.next = next;
            state.memberFollowUpData.previous = previous;
            state.memberFollowUpData.utilities = utilities;
            state.memberFollowUpData.membersList = membersList;
            state.memberFollowUpData.currentPage = page;
            state.memberFollowUpData.page_size = rowsPerPage;
        },

        // set agent requirements data
        setRequirementsData(state, action) {
            const { count = 0, next = null, previous = null, results = {}, page = 0, page_size: rowsPerPage = 0 } = action.payload;
            const { utilities = {}, requirement_list: requirementList = [] } = results || {};
            const { filters: reqFilters = [] } = utilities || {};
            const { current_status: statusArray = [] } = reqFilters[0] || [];
            statusArray.unshift({ id: 0, name: 'All Requirements' });
            devConsolelog('follow up data23', statusArray);
            state.requirementsData.count = count;
            state.requirementsData.next = next;
            state.requirementsData.previous = previous;
            state.requirementsData.utilities = utilities;
            state.requirementsData.requirementList = requirementList;
            state.requirementsData.currentPage = page;
            state.requirementsData.page_size = rowsPerPage;
        },

        // set filters
        setAgentFilters(state, action) {
            state.agentFollowUpFilters = action.payload;
            // state.data.currentPage = 0;
        },

        // set filters
        setRequirementFilters(state, action) {
            state.requirementFilters = action.payload;
            // state.data.currentPage = 0;
        }
    }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function getAcmDashboardData() {
    return async () => {
        try {
            const response = await axios.get(`${ARYABHATTA_API_DOMAIN_V1}acm/dashboard/`, {
                headers: {
                    source: 'mweb'
                }
            });

            dispatch(slice.actions.setData(response.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getAgentFollowUpsData(pageChangeUrl = '', filterType = '', newPage = 0) {
    return async () => {
        let url = `${ARYABHATTA_API_DOMAIN_V1}acm/members/follow-ups/`;
        if (pageChangeUrl) {
            url = pageChangeUrl;
        }
        if (filterType) {
            const filterQueryString = returnFiltersQueryString(filterType);

            url = `${url}?${filterQueryString}`;
        }
        try {
            const response = await axios.get(url, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });

            dispatch(slice.actions.setAgentFollowUpData({ ...response.data, page: filterType ? 0 : newPage }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getAcmRequirementsData(pageChangeUrl = '', filterType = '', newPage = 0) {
    return async () => {
        let url = `${ARYABHATTA_API_DOMAIN_V1}acm/members/requirements/`;
        if (pageChangeUrl) {
            url = pageChangeUrl;
        }
        if (filterType) {
            const filterQueryString = returnFiltersQueryString(filterType);

            url = `${url}?${filterQueryString}`;
        }
        try {
            const response = await axios.get(url, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });

            dispatch(slice.actions.setRequirementsData({ ...response.data, page: filterType ? 0 : newPage }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export const filterAgentsData =
    (filter = '') =>
    async () => {
        dispatch(slice.actions.setAgentFilters(filter));
    };

export const filterRequirementsData =
    (filter = '') =>
    async () => {
        dispatch(slice.actions.setRequirementFilters(filter));
    };
